/** AUTO-GENERATED FROM capabilities.spec.yaml — DO NOT EDIT MANUALLY. Run pnpm codegen:capabilities to regenerate. */
/**
 * ROLE_CAPABILITY_MAPPING — Fatia 2-bis (16/Mai/2026).
 *
 * **Fonte de verdade** sobre quais capabilities cada `primaryRole` recebe
 * por default ao resolver `User → tokens`. Filtro aplicado em
 * `AuthService.resolveCapabilitiesForUser` e re-checado em runtime por
 * `CapabilityResolverService.resolveCapability` (defense-in-depth).
 *
 * **Modelo capability-based preservado** — esta tabela é apenas filtro de
 * **resolução por papel**. ACL granular contextual (Bond-level) segue ADR-039
 * fora desta fatia.
 *
 * **`additionalCapabilities[]` do User é aditivo** — sobrescreve filtro de
 * papel para casos específicos (admin atribui capability extra).
 *
 * **Capabilities core** sempre concedidas (não filtradas por papel):
 *   `core-authentication`, `core-conversation`, `core-multi-tenant`,
 *   `core-multi-channel-whatsapp`, `audit-basic`.
 *
 * **Capabilities `isInternal: true`** filtradas separadamente: apenas
 * papéis `*_lume` recebem (filtro upstream em catalog `isInternal`).
 *
 * Vide ficha `governance/cronograma/Fatia_02-bis_role_based_capability_filter.md`
 * §8 — mapeamento aprovado pelo Tech Lead.
 */
/**
 * Capabilities core — todos os papéis tenants recebem incondicionalmente,
 * desde que tier do tenant permita (`audit-basic` é Solo+; `core-*` é Solo+).
 * `*_lume` também recebe core (operacionalmente trabalha sobre Lume Engine).
 */
export const CORE_CAPABILITIES = new Set([
    'core-authentication',
    'core-conversation',
    'core-multi-tenant',
    'core-multi-channel-whatsapp',
    'audit-basic',
]);
// ───────────────────────────────────────────────────────────────────────────
// Por papel — acréscimos sobre CORE_CAPABILITIES.
// ───────────────────────────────────────────────────────────────────────────
const ATENDENTE_EXTRA = [
    'bond-basic',
    'bond-create',
    'documents-basic',
    'personal-snippets',
    'ai-suggestion-h-a',
    'ai-summary-basic',
    'ai-classification-basic',
    'ai-protection-active',
    // Sprint Backend Protection v1 — Item 225 canonical. Solo+ (todo atendente
    // protege PII pré-envio independente do tier). Policy de uso (block/warn/pass)
    // modula via tier do tenant, não o gate de capability.
    'ai-protection-outbound-validate',
    'ai-inbound-trigger-detection',
    'conversation-commitments',
    'operator-daily-ritual',
    'client-ai-engagement-policy',
    'proactive-work-suggestions',
    'ai-context-adversarial-filter',
    // Fatia 8 — Team+ via tier-filter (atendente em Solo não recebe; tier corta).
    'ai-classification-with-history',
    // Fatia E — checklists básicos (Team+ via tier-filter).
    'checklists-basic',
    // Fatia F — core lifecycle (Solo+ não-opcional; atendente fecha conversa).
    'conversation-lifecycle-strict',
    // Fatia G — peer-coordination (ADR-049 + Caderno cap 39). 3 capabilities
    // do atendente Team+ via tier-filter (Solo não recebe; co-attendance fica
    // em SUPERVISOR_EXTRA pois exige Professional+).
    'peer-coordination-handoff',
    'peer-coordination-presence',
    'peer-coordination-internal-notes',
    'person-read',
    // Sprint Backend Operacional Brief 2A r7b — Appointment (ADR-115).
    // Solo+ via tier-filter; atendente vê/cria/edita appointments na Conversa.
    'appointments',
    'scheduling-native',
    'scheduling-waitlist',
    'commitments',
    'pendencies',
    'playbook-read',
    'persona-read',
    'ai-conduct-profile-read',
    'ai-routing',
    'user-preferences-manage',
    'notifications-manage',
    'behavioral-pattern-contest',
    'initiate-proactive-conversation',
    'initiate-utility',
    'initiate-service',
    'initiate-customer-care',
    'inbox-saved-views',
    'inbox-shared-views',
    'agent-dashboard-read',
    'structured-collection',
    'ai-collection-extraction',
    // S5 (Condução Governada) — delegação por etapa (ADR-154). Ação de front-line: o
    // operador (ex.: vendedor NetComet) delega a coleta à IA. Professional+ via tier-filter
    // (roda sobre H_c; decisão TL 11/Jul). Espelha o alcance da delegação existente
    // (conversation-lifecycle-strict, atendente) + gate de tier da capability.
    'ai-step-delegation',
    'customer-registry-confirm',
    'conversation-sequences',
    'direct-message-participate',
    'direct-message-initiate-upward',
    'playbook-manual-switch',
    'canned-response-use',
    // Frente voz/STT Fase 1 (18/Jul/2026) — ditado no composer mobile. Solo+
    // universal; opt_in_required (CapabilityEntry per-tenant) mantém inerte fora
    // do opt-in. Supervisor herda via extends.
    'voice-dictation',
    // Item 556 (decisão do TL, 24/Out/2026) — eram órfãs (só por concessão manual). Atendente e acima.
    // As 3 são `opt_in_required`: seguem inertes no tenant sem adesão (a adesão é por CLASSE — Item 433).
    'self-restriction-declare',
    'self-restriction-read',
    'message-content-search',
];
/**
 * MOBILE_BASE_EXTRA — Caminho A pré-Sprint Mobile Code (ADR-136).
 *
 * 11 capabilities atribuídas a todos os papéis tenants que operam mobile
 * (atendente/supervisor/gestor/admin_tenant + DPO).
 *
 * Fonte canônica visual: `apps/lume-atendimento/design-references/brief-6/r1/shared/fixtures-6.js`
 * MOBILE_BASE_EXTRA (cristalizado em main pós Brief 6 V2 R1-R3 mergeados).
 *
 * Distribuição:
 * - 6 mobile lifecycle (ADR-116): `mobile-app-access`, `mobile-app-multi-device`,
 *   `mobile-app-push-notifications`, `mobile-app-push-customize-types`,
 *   `mobile-biometric-login`, `mobile-offline-cache`.
 * - 5 auth/profile (Item 58): `profile-photo-edit`, `sso-google`, `sso-apple`,
 *   `sso-microsoft`, `two-factor-auth`.
 *
 * Tier-filter corta capabilities cujo `tierMin` excede o tier do tenant
 * (ex: `sso-microsoft` Pro+ corta tenants Team).
 */
const MOBILE_BASE_EXTRA = [
    'mobile-app-access',
    'mobile-app-multi-device',
    'mobile-app-push-notifications',
    'mobile-app-push-customize-types',
    'mobile-biometric-login',
    'mobile-offline-cache',
    'profile-photo-edit',
    'sso-google',
    'sso-apple',
    'sso-microsoft',
    'two-factor-auth',
];
const SUPERVISOR_EXTRA = [
    ...ATENDENTE_EXTRA,
    'silent-monitoring',
    // D6 Auditoria 19/Mai/2026 — Item 138 RESOLVIDO. Painel de Equipe Brief 3 T1
    // (cards atendentes + presence + SLA breach + sentiment trend). Team+ via tier-filter.
    'supervisor-team-panel',
    'team-config-read',
    'customer-portfolio-read',
    'audit-events-read',
    'users-read',
    'users-update',
    'ai-conductor-h-b',
    'behavioral-analysis-full',
    // Fatia 8 — Supervisor consome insights comportamentais + risk score (Professional+).
    'ai-behavioral-analysis',
    'ai-risk-score-cumulative',
    // Fatia E — checklist-agent (Pro+ via tier-filter).
    'checklists-with-agent',
    // Fatia F — supervisor cria/lê Groups + lê Sequences com flags (Team+).
    'conversation-groups',
    // Fatia G — co-attendance é Professional+ (sênior acompanha júnior, supervisor
    // monitora discretamente, especialista entra). Cascade automático para
    // gestor/admin via ...SUPERVISOR_EXTRA em níveis superiores.
    'peer-coordination-co-attendance',
    'commitments-full',
    'reopen-closed-conversations',
    'audit-chain-replay',
    'agent-performance-read',
    'notifications-admin',
    'pendency-team-read',
    'pendency-bulk-operations',
    'sla-read',
    'sla-manage',
    'sla-pattern-detection',
    'coattendance-shadow-observer',
    'coattendance-invisible-to-all',
    'coattendance-collaborative-observer',
    'supervisor-session-notes',
    'force-takeover',
    'force-takeover-bypass-coattendance',
    'conversation-assign',
    'direct-message-initiate',
    'force-playbook-switch',
    'behavioral-pattern-read',
    'behavioral-pattern-review',
    'quality-aggregations-read',
    'quality-aggregations-export',
    'quality-coach-scheduling',
    'conflict-alert-mitigate',
    'conflict-alert-accept-with-justification',
    'conflict-alert-metrics-read',
    'runbook-trigger',
    'runbook-execution-read',
    'p1-read',
    'p1-acknowledge',
    'p1-runbook-trigger',
    'dual-approval-first-approve',
    'override-ai-required-validation',
    'override-carencia-proativa',
    'override-gatilho-protecao',
    'override-conflict-alert-accept',
    'ai-insight-supervisor',
    'ai-insight-action',
    'ai-insight-manual-trigger',
    'supervisor-dashboard-read',
    'supervisor-dashboard-customize',
    'canned-response-manage',
    'media-library-manage',
    'canned-response-analytics',
    // Item 556 (decisão do TL, 24/Out/2026) — leitura de grafo de papéis, autoridade de decisão,
    // visibilidade e prazos: supervisor e acima (gestor e admin herdam). `opt_in_required` (Item 433).
    'role-graph-read',
    'decision-authority-read',
    'visibility-graph-read',
    'deadline-taxonomy-read',
];
const GESTOR_EXTRA = [
    ...SUPERVISOR_EXTRA,
    'multi-channel-extended',
    'users-create',
    'users-deactivate',
    'users-invite',
    'ai-conductor-h-c',
    'ai-conductor-escalation',
    'identity-merge-confirm-by-operator',
    'documents-versioning',
    'documents-aggregated-view',
    'ai-document-summary',
    'documents-redaction',
    'acl-granular-bond-level',
    'confidentiality-bubbles-full',
    'conflict-of-interest-detection',
    'conflict-of-interest-blocking',
    'tenant-config-read',
    'capability-registry-read',
    'checklist-system',
    // PR-B Fatia 8 — Gestor (Pro+) consome o painel de consumo de IA do tenant.
    // B-1 (17/Set/2026): era 'ai-cost-monitoring', que trazia junto o custo da
    // plataforma. O custo mudou de casa (platform-ai-cost-read).
    'ai-usage-read',
    // Caminho A pré-Sprint Mobile Code — ADR-136. Gestor (Pro+ via tier-filter)
    // revoga sessions remotamente (multi-operador) + recebe alertas de anomaly.
    'mobile-app-admin-remote-logout',
    'mobile-app-session-anomaly-detection',
    'tenant-lifecycle-read',
    'playbook-author',
    'persona-author',
    'auto-reply-out-of-hours',
    'proactive-verification',
    'appointment-reminders',
    'appointment-confirmation',
    'message-template-read',
    'message-template-manage',
    'bond-type-config-read',
    'bond-type-config-manage',
    'team-config-manage',
    'customer-portfolio-manage',
    'p1-escalate-chain',
    'p1-manual-flag',
    'dual-approval-second-approve',
    'dual-approval-request-create',
    'override-modo-operacao',
    'override-capability-temporaria',
    'override-legal-capacity-exception',
    'override-h-c-adversarial-strict',
    'ai-insight-gestor',
    'ai-billing-read',
];
const ADMIN_TENANT_EXTRA = [
    ...GESTOR_EXTRA,
    'ai-billing-manage',
    'tenant-config-manage',
    'capability-registry-manage',
    'legal-capacity-management',
    'documents-digital-signing',
    // Fatia E — bloqueio de fechamento (Regulated only via tier-filter).
    'checklists-block-closure',
    'users-impersonate',
    'ai-byok-manage',
    'knowledge-base-author',
    'tenant-lifecycle-transition',
    'tenant-lifecycle-churning-initiate',
    'custom-model-selection',
    'integrations-manage',
    'auto-reply-out-of-hours',
    'ai-auto-close-h-c',
    'ai-transition',
    'proactive-verification',
    'appointment-reminders',
    'appointment-confirmation',
    'ai-typing-indicator',
    'external-context-sync',
    'notifications-send',
    'conflict-alert-resolve-or-revoke',
    'mcp-blocking-bypass',
    'runbook-manage',
    'customer-registry-import',
    // Item 556 (decisão do TL, 24/Out/2026) — escrita sensível (política de visibilidade, reunião
    // reservada entre partes, vínculo de cliente): só admin. `opt_in_required` (Item 433).
    'visibility-policy-manage',
    'conversation-caucus-manage',
    'customer-link-establish',
];
/**
 * DPO Tenant — foco em conformidade LGPD, NÃO recebe capabilities
 * operacionais (bond-basic, ai-suggestion-h-a, etc.). Não atende conversas.
 */
const DPO_TENANT_EXTRA = [
    'lgpd-requests-management',
    'lgpd-anonymization-execute',
    'lgpd-requests-read',
    'lgpd-requests-create',
    'lgpd-requests-approve',
    'lgpd-requests-execute',
    'lgpd-legal-holds-manage',
    'lgpd-authorized-contacts-read',
    'lgpd-authorized-contacts-manage',
    'audit-events-read',
    'audit-events-read-sensitive',
    'legal-capacity-management',
    'documents-acl-strict',
    'documents-redaction',
    // PR-B Fatia D Item 2 — Q2 Tech Lead 18/Mai/2026: DPO-only revert.
    // Apply Pro+ (Gestor/Admin/DPO); revert Regulated → exclusivo DPO.
    'documents-redaction-revert',
    // Item 55 (D-F do verifier Brief 5 V2 R4, 17/Mai/2026) — DPO acessa
    // B3 (Centro de Conformidade — ADR-117 ConflictAlert dashboard).
    // Matriz canônica declara DPO como acessante primário; sem este
    // capability, DPO ficava bloqueado contrariando a Matriz.
    'conflict-of-interest-detection',
    'conflict-of-interest-blocking',
    // Fatia E — DPO Regulated valida bloqueio de fechamento por critical
    // pendente (compliance LGPD — auditoria de cobertura de checklist).
    'checklists-block-closure',
    // Caminho A pré-Sprint Mobile Code — ADR-136. DPO (Pro+ via tier-filter)
    // audita anomaly detection em UserSessions para investigação forense
    // (login geo-distante, device novo desconhecido, IP suspeito).
    'mobile-app-session-anomaly-detection',
    'tenant-lifecycle-read',
    'tenant-lifecycle-churning-initiate',
    'integrations-read',
    'audit-chain-replay',
    'user-preferences-manage',
    'notifications-manage',
    'runbook-execution-read',
];
/**
 * Papéis Lume (`suporte_lume`, `engenharia_lume`, `direcao_lume`) — recebem
 * lista igual em v1. Refinamento granular quando produto operar multi-tenant
 * real. Acesso a `isInternal: true` é filtrado separadamente em catalog.
 */
const LUME_EXTRA = [
    'lume-internal-capability-management',
    'user-preferences-manage',
    'notifications-manage',
    'platform-ai-cost-read',
    'platform-tenants-read',
    'platform-tenants-manage',
    'platform-ai-pricing-read',
    'platform-ai-pricing-manage',
];
// ───────────────────────────────────────────────────────────────────────────
// Mapping canônico.
// ───────────────────────────────────────────────────────────────────────────
export const ROLE_CAPABILITY_MAPPING = Object.freeze({
    atendente: new Set([...CORE_CAPABILITIES, ...ATENDENTE_EXTRA, ...MOBILE_BASE_EXTRA]),
    supervisor: new Set([...CORE_CAPABILITIES, ...SUPERVISOR_EXTRA, ...MOBILE_BASE_EXTRA]),
    gestor: new Set([...CORE_CAPABILITIES, ...GESTOR_EXTRA, ...MOBILE_BASE_EXTRA]),
    admin_tenant: new Set([...CORE_CAPABILITIES, ...ADMIN_TENANT_EXTRA, ...MOBILE_BASE_EXTRA]),
    dpo_tenant: new Set([...CORE_CAPABILITIES, ...DPO_TENANT_EXTRA, ...MOBILE_BASE_EXTRA]),
    suporte_lume: new Set([...CORE_CAPABILITIES, ...LUME_EXTRA]),
    engenharia_lume: new Set([...CORE_CAPABILITIES, ...LUME_EXTRA]),
    direcao_lume: new Set([...CORE_CAPABILITIES, ...LUME_EXTRA]),
});
// ───────────────────────────────────────────────────────────────────────────
// Helpers — lookup O(1).
// ───────────────────────────────────────────────────────────────────────────
/**
 * Retorna o conjunto de capabilities concedidas a um papel por default.
 * Não inclui `additionalCapabilities[]` do User (que é aditivo upstream).
 */
export function getCapabilitiesForRole(role) {
    return ROLE_CAPABILITY_MAPPING[role] ?? new Set();
}
/**
 * Verifica se um papel tem uma capability por default.
 * Lookup O(1). Não considera `additionalCapabilities[]`.
 */
export function roleHasCapability(role, code) {
    return ROLE_CAPABILITY_MAPPING[role]?.has(code) ?? false;
}
/**
 * Razões canônicas para negação de capability — usadas em audit
 * `capability.denied` payload + ForbiddenException response.
 */
export const CAPABILITY_DENIAL_REASONS = {
    ROLE_FILTER: 'role-filter',
    TIER_FILTER: 'tier-filter',
    IS_INTERNAL: 'is-internal',
    CROSS_TENANT: 'cross-tenant',
    USER_NOT_FOUND: 'user-not-found',
    USER_INACTIVE: 'user-inactive',
    CAPABILITY_NOT_FOUND: 'capability-not-found',
    /**
     * Sprint A4 S1 PR 1 — opt-in real (Item 312).
     * Capability tem opt_in_required=true mas CapabilityEntry per-tenant
     * está ausente ou status='suspended' — granted=false (regra do silêncio).
     */
    OPT_IN_SUSPENDED: 'opt-in-suspended',
};
//# sourceMappingURL=role-mapping.js.map